import { Link, useLocation } from 'react-router';
import { BarChart3, Folder, Plus, Link2, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

interface BottomNavProps {
  onUploadClick: () => void;
  onMenuClick: () => void;
}

const navItems = [
  { name: 'Home', href: '/dashboard', icon: BarChart3 },
  { name: 'Folders', href: '/folders', icon: Folder },
  { name: 'Upload', href: '#upload', icon: Plus, isAction: true },
  { name: 'Links', href: '/share-links', icon: Link2 },
  { name: 'More', href: '#menu', icon: Menu, isMenu: true },
];

export function BottomNav({ onUploadClick, onMenuClick }: BottomNavProps) {
  const location = useLocation();
  const { tenant } = useAuth();
  const primaryColor = tenant?.branding_primary_color || '#2563eb';

  return (
    <nav
      className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 lg:hidden z-50"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex justify-around items-stretch h-16">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href ||
            location.pathname.startsWith(item.href + '/');

          // Upload action button (centered, prominent)
          if (item.isAction) {
            return (
              <button
                key={item.name}
                onClick={onUploadClick}
                className="flex flex-col items-center justify-center flex-1 min-w-[44px] active:bg-blue-50"
                aria-label="Upload files"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center -mt-4 shadow-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  <item.icon className="h-6 w-6 text-white" />
                </div>
              </button>
            );
          }

          // More menu button
          if (item.isMenu) {
            return (
              <button
                key={item.name}
                onClick={onMenuClick}
                className="flex flex-col items-center justify-center flex-1 min-w-[44px] min-h-[44px] text-gray-600 active:bg-gray-50"
                aria-label="Open menu"
              >
                <item.icon className="h-6 w-6" />
                <span className="text-xs mt-1">{item.name}</span>
              </button>
            );
          }

          // Standard nav link
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex flex-col items-center justify-center flex-1',
                'min-w-[44px] min-h-[44px] active:bg-gray-50',
                isActive ? 'text-blue-600' : 'text-gray-600'
              )}
              style={isActive ? { color: primaryColor } : undefined}
            >
              <item.icon className="h-6 w-6" />
              <span className="text-xs mt-1">{item.name}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
